import AgTypography from '../../../Styles/AgTypography';
import AgSimpleTable, { ProfileSectionTableData } from '../../AgSimpleTable';
import { AgColor } from '../../../Styles/AgColor';
import { useIntl } from 'react-intl';
import AgIconButton from '../../../AgIconButton';
import { ReactNode, useEffect, useState } from 'react';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CloseIcon from '@mui/icons-material/Close';

export interface ToastListProps {
  message: string;
  isError?: boolean;
  open?: boolean;
}

export interface SummaryCardsProps {
  tableData: ProfileSectionTableData[];
  title?: string;
  numberOfAllFields?: number;
  numberOfFinishedFields?: number;
  btnIcon?: ReactNode;
  btnLabel?: string;
  btnOnClick?: () => void;
  toastList?: ToastListProps[];
  missingMandatoryFields?: string[];
}

const AgSummaryCards = (
  {
    tableData,
    title,
    numberOfFinishedFields,
    numberOfAllFields,
    btnIcon,
    btnLabel,
    btnOnClick,
    toastList,
    missingMandatoryFields
  }: SummaryCardsProps
) => {
  const messages = useIntl().formatMessage;
  const [list, setList] = useState<ToastListProps[]>([]);
  const [tableDataWithMissingField, setTableDataWithMissingField] = useState<ProfileSectionTableData[]>([]);

  useEffect(()=>{
    if (!toastList?.length) return;
    setList(toastList);
  },[toastList]);
  
  useEffect(()=>{
    if (!tableData?.length) return;
    tableData.forEach(item => {
      if (missingMandatoryFields?.includes(item.name)) {
        item.isMissed = true;
      }
    });
    setTableDataWithMissingField(tableData);
  },[missingMandatoryFields, tableData]);

  if (!numberOfAllFields) return null;

  return (
    <AgTypography
      style={{
        display: 'grid',
        gap: '12px',
        padding: '12px 16px',
        borderRadius: '8px',
        border: '0.75px solid',
        borderColor: AgColor.grey3
      }}
    >
      <AgTypography
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <AgTypography>
          {title &&
            <AgTypography
              contentStyle={'AP.H5.Bold'}
              message={title}
              style={{
                color: AgColor.grey6
              }}
            />
          }
          {numberOfAllFields?
            <AgTypography
              contentStyle={'AP.H5.Bold'}
              message={numberOfFinishedFields + '/' + numberOfAllFields + messages({ id: 'MEMBER_MANDATORY_FIELDS' })}
              style={{
                color: numberOfFinishedFields === numberOfAllFields ? AgColor.success2 : AgColor.errorMain
              }}
            /> : null}
        </AgTypography>
        {(btnIcon || btnLabel) &&
          <AgIconButton
            icon={btnIcon}
            type={'secondary'}
            label={btnLabel}
            onClick={btnOnClick}
          />
        }
      </AgTypography>
      <AgSimpleTable tableData={tableDataWithMissingField} />
      {list?.map((toast, index) => {
        return (toast.open &&
          <AgTypography
            key={index}
            style={{
              display: 'flex',
              gap: '12px',
              padding: '12px 16px',
              borderRadius: '6px',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: toast.isError ? AgColor.errorMain : AgColor.link1
            }}
          >
            <AgTypography
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'start',
                alignItems: 'center',
              }}
            >
              {toast.isError
                ? <ErrorOutlineOutlinedIcon sx={{ width: '20px', height: '20px', color: AgColor.grey0 }} />
                : <WarningAmberOutlinedIcon sx={{ width: '20px', height: '20px', color: AgColor.grey0 }} />
              }
              <AgTypography
                contentStyle={'AP.Paragraph.Large.SemiBold'}
                message={toast.message}
                style={{ color: AgColor.grey0 }}
              />
            </AgTypography>
            {!toast.isError &&
              <AgTypography
                onClick={() => {
                  const updatedList = [...list];
                  updatedList[index].open = false;
                  setList(updatedList);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <CloseIcon fontSize='small' sx={{ color: AgColor.grey0, cursor: 'pointer' }} />
              </AgTypography>
            }
          </AgTypography>
        );
      })}
    </AgTypography>
  );
};

export default AgSummaryCards;
